import { useRef, useState, type ReactNode } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { LANG_LABEL, LANG_FLAG, type Lang } from '@/i18n/translations';
import { useT } from '@/i18n/useI18n';
import { audio } from '@/audio/AudioService';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi } from '@/auth/authApi';
import { disconnectSocket } from '@/net/socket';
import { teardownPush } from '@/push/pushService';
import { Avatar } from '@/art';
import { BackButton } from '@/components/BackButton';
import { useAppVersion } from '@/version/appVersion';

export function SettingsScreen() {
  const go = useUIStore((s) => s.go);
  const settings = useSettingsStore();
  const t = useT();
  const appVersion = useAppVersion();
  const resetUser = useUserStore((s) => s.reset);
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const clearAuth = useAuthStore((s) => s.clear);
  const [deleting, setDeleting] = useState(false);

  const onLogout = () => {
    void teardownPush();
    clearAuth();
    disconnectSocket();
    resetUser();
    go('login');
  };

  const onDeleteAccount = async () => {
    if (deleting || !token) return;
    // Two-step confirmation guards against accidental, irreversible deletion.
    if (!confirm(t('settings.confirm_delete_account'))) return;
    if (!confirm(t('settings.confirm_delete_account_final'))) return;
    setDeleting(true);
    try {
      await authApi.deleteAccount(token);
      void teardownPush();
      clearAuth();
      disconnectSocket();
      resetUser();
      go('login');
    } catch {
      setDeleting(false);
      alert(t('settings.delete_account_failed'));
    }
  };

  return (
    <div
      className="screen"
      style={{
        background: 'linear-gradient(180deg, var(--bg-mid) 0%, var(--bg-deep) 100%)',
      }}
    >
      <div className="hex-bg" />

      <div style={{ padding: '14px 18px 8px' }}>
        <BackButton onClick={() => go('home')} />
        <div className="display" style={{ fontSize: 26, marginTop: 10 }}>
          {t('settings.title')}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px 24px' }}>
        <SettingsGroup title={t('settings.language')}>
          <LanguagePicker value={settings.lang} onChange={settings.setLang} />
        </SettingsGroup>

        <SettingsGroup title={t('settings.audio')}>
          <SettingsSlider label={t('settings.sfx')} value={settings.sfx} onChange={settings.setSfx} />
          <SettingsSlider label={t('settings.music')} value={settings.music} onChange={settings.setMusic} />
          <SettingsToggle
            label={t('settings.haptic')}
            value={settings.haptic}
            onChange={settings.setHaptic}
          />
        </SettingsGroup>

        <SettingsGroup title={t('settings.gameplay')}>
          <SettingsToggle
            label={t('settings.lefty')}
            value={settings.lefty}
            onChange={settings.setLefty}
          />
          <SettingsToggle
            label={t('settings.colorblind')}
            value={settings.colorBlind}
            onChange={settings.setColorBlind}
          />
          <SettingsSlider
            label={t('settings.tap_zone')}
            value={settings.tapZone}
            min={70}
            max={130}
            unit="%"
            onChange={settings.setTapZone}
          />
        </SettingsGroup>

        <SettingsGroup title={t('settings.avatar')}>
          <AvatarUploader />
        </SettingsGroup>

        <SettingsGroup title={t('settings.account')}>
          <SettingsRow
            label={user?.isGuest ? t('settings.guest_id') : t('settings.username')}
            value={user?.username ?? user?.id.slice(0, 12) ?? '—'}
          />
          {user?.email && <SettingsRow label={t('settings.email')} value={user.email} />}
          <SettingsRow label={t('settings.region')} value="VN · SEA-1" />
          <SettingsRow label={t('settings.version')} value={appVersion} />
        </SettingsGroup>

        <div
          style={{
            marginTop: 20,
            padding: 14,
            border: '1px solid rgba(230,57,70,0.3)',
            borderRadius: 12,
            background: 'rgba(230,57,70,0.05)',
          }}
        >
          <div
            className="display"
            style={{ fontSize: 12, color: 'var(--c-red)', letterSpacing: '0.08em' }}
          >
            {t('settings.danger_zone')}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-muted)',
              marginTop: 4,
              marginBottom: 10,
            }}
          >
            {user?.isGuest ? t('settings.danger_guest') : t('settings.danger_user')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              className="btn btn-ghost"
              onClick={() => {
                if (confirm(t('settings.confirm_signout'))) onLogout();
              }}
              style={{
                color: 'var(--c-red)',
                borderColor: 'rgba(230,57,70,0.3)',
                fontSize: 11,
              }}
            >
              {t('settings.sign_out')}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => void onDeleteAccount()}
              disabled={deleting}
              style={{
                color: '#fff',
                background: 'rgba(230,57,70,0.85)',
                borderColor: 'rgba(230,57,70,0.6)',
                fontSize: 11,
                cursor: deleting ? 'wait' : 'pointer',
              }}
            >
              {deleting ? '…' : t('settings.delete_account')}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}

function SettingsGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="tag" style={{ padding: '0 4px 8px' }}>
        {title}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

interface SettingsSliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  unit?: string;
}

function SettingsSlider({ label, value, onChange, min = 0, max = 100, unit }: SettingsSliderProps) {
  return (
    <div style={{ padding: '14px 14px', borderBottom: '1px solid var(--hairline)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13 }}>{label}</span>
        <span className="mono" style={{ fontSize: 12, color: 'var(--c-yellow)' }}>
          {value}
          {unit ?? '%'}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(+e.target.value)}
        style={{ width: '100%', accentColor: 'var(--c-yellow)' }}
      />
    </div>
  );
}

interface SettingsToggleProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

function SettingsToggle({ label, value, onChange }: SettingsToggleProps) {
  return (
    <div
      onClick={() => { audio.play('ui.toggle'); onChange(!value); }}
      style={{
        padding: '14px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid var(--hairline)',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: 13 }}>{label}</span>
      <div
        style={{
          width: 44,
          height: 24,
          borderRadius: 14,
          background: value ? 'var(--c-yellow)' : 'rgba(255,255,255,0.1)',
          position: 'relative',
          transition: 'background 0.2s',
          boxShadow: value
            ? '0 0 10px rgba(244,199,36,0.5)'
            : 'inset 0 1px 2px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: value ? 22 : 2,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
      </div>
    </div>
  );
}

function AvatarUploader() {
  const t = useT();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const token = useAuthStore((s) => s.token);
  const setProfile = useUserStore((s) => s.setProfile);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pick = async (file: File) => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      const { url } = await authApi.uploadAvatar(token, file);
      // Refresh auth/profile so HomeScreen + match HUD pick up new photo.
      const { user: fresh } = await authApi.me(token);
      setUser(fresh);
      setProfile({ avatarUrl: fresh.avatarUrl });
      void url;
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removePhoto = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await authApi.deleteAvatar(token);
      const { user: fresh } = await authApi.me(token);
      setUser(fresh);
      setProfile({ avatarUrl: null });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 14, display: 'flex', gap: 14, alignItems: 'center' }}>
      <Avatar
        color={user?.avatarColor ?? 'red'}
        size={64}
        photoUrl={user?.avatarUrl ?? null}
        characterUrl={user?.characterIdleUrl ?? null}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: 'var(--ink)', marginBottom: 4 }}>
          {user?.avatarUrl ? t('settings.avatar_custom') : user?.characterIdleUrl ? t('settings.avatar_character') : t('settings.avatar_default')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginBottom: 8 }}>
          {t('settings.avatar_formats')}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="btn btn-ghost"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            style={{ fontSize: 10, padding: '6px 10px' }}
          >
            {busy ? '…' : user?.avatarUrl ? t('settings.avatar_change') : t('settings.avatar_upload')}
          </button>
          {user?.avatarUrl && (
            <button
              className="btn btn-ghost"
              onClick={removePhoto}
              disabled={busy}
              style={{
                fontSize: 10,
                padding: '6px 10px',
                color: 'var(--c-red)',
                borderColor: 'rgba(230,57,70,0.3)',
              }}
            >
              {t('settings.avatar_remove')}
            </button>
          )}
        </div>
        {err && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--c-red)' }}>{err}</div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
        }}
      />
    </div>
  );
}

function SettingsRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: '14px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid var(--hairline)',
      }}
    >
      <span style={{ fontSize: 13 }}>{label}</span>
      <span className="mono" style={{ fontSize: 11, color: 'var(--ink-muted)' }}>
        {value}
      </span>
    </div>
  );
}

function LanguagePicker({ value, onChange }: { value: Lang; onChange: (l: Lang) => void }) {
  const langs: Lang[] = ['en', 'vi', 'zh', 'ja', 'ko', 'th'];
  return (
    <div style={{ padding: '8px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {langs.map((l) => {
        const active = l === value;
        return (
          <button
            key={l}
            onClick={() => onChange(l)}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: `1px solid ${active ? 'var(--c-cyan)' : 'var(--hairline)'}`,
              background: active ? 'rgba(34,211,238,0.18)' : 'rgba(0,0,0,0.3)',
              color: active ? 'var(--c-cyan)' : 'var(--ink)',
              fontFamily: 'var(--f-display)',
              fontSize: 12,
              letterSpacing: '0.05em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 44,
            }}
          >
            <span style={{ fontSize: 18 }}>{LANG_FLAG[l]}</span>
            <span>{LANG_LABEL[l]}</span>
            {active && <span style={{ marginLeft: 'auto', fontSize: 14 }}>✓</span>}
          </button>
        );
      })}
    </div>
  );
}
